import { Stack, Typography } from '@sureco/design-system'
import { Button, Spin, Tag } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useMemo, useState } from 'react'
import { browserHistory } from 'react-router'
import { DEMO_COMPANY_MANAGEMENT, demoCompanyEditPath } from '../paths'
import DemoBanner from '../DemoCustomer/components/DemoBanner'
import DemoCustomerSummary from '../DemoCustomer/components/DemoCustomerSummary'
import DemoLoginCredentials from '../DemoCustomer/components/DemoLoginCredentials'
import DemoPresetsModal from '../DemoCustomer/components/DemoPresetsModal'
import DemoSetupProgress from '../DemoCustomer/components/DemoSetupProgress'
import QuoteFileUploadSection from '../DemoCustomer/components/QuoteFileUploadSection'
import { useDemoContributionStatus } from '../DemoCustomer/hooks/useDemoContributionStatus'
import { useDemoPopulationStatus } from '../DemoCustomer/hooks/useDemoPopulationStatus'
import { DemoCustomerConfig } from '../DemoCustomer/types'
import { IconStatusMapper } from 'enrollment-platform/pages/CustomerManagement/pages/ListCustomer/hooks/useListCustomer'
import useEnrollmentConfiguration from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Enrollment/hooks/useEnrollmentConfiguration'
import useViewDemoCompany from '../DemoCustomer/hooks/useViewDemoCompany'

interface ViewDemoCustomerProps {
  params: { id: string }
}

/**
 * Demo customer view.
 *
 * All fields come from live APIs (same endpoints as ViewCustomer):
 *  - Name / status / payrollCycle / contacts → GET /customers/:id
 *  - Enrollment dates                        → GET /customers/:id/configuration/enrollment
 *  - Contribution status                     → GET /customers/:id/demo/contribution-status
 */
export default function ViewDemoCustomer({ params }: ViewDemoCustomerProps) {
  const { id } = params

  const { data, isLoading } = useViewDemoCompany({ id })

  // Enrollment windows sorted by benefitEffectiveDate desc.
  // The first (most recent) record holds the live dates.
  const { getEnrollmentConfigurationQuery } = useEnrollmentConfiguration(id)
  const latestEnrollment = getEnrollmentConfigurationQuery.data?.[0]
  const enrollmentLoading = getEnrollmentConfigurationQuery.isLoading

  // Population status — survives page reloads by querying the member count.
  const {
    employeeCount,
    isPopulated,
    isLoading: populationLoading,
  } = useDemoPopulationStatus(id)

  // Demo contribution run status — polls while the run is in progress.
  const { contributionStatus, isLoading: contributionsLoading } =
    useDemoContributionStatus(id)
  // Populate succeeded but no contribution run could be queued.
  const [runNotStarted, setRunNotStarted] = useState(false)

  // This page re-renders in place when the route id changes, so the flag has
  // to be cleared explicitly — otherwise the previous company's failed run
  // keeps showing here. Reset during render rather than in an effect so the
  // new company never paints a frame of the old one's failure.
  const [renderedId, setRenderedId] = useState(id)
  if (renderedId !== id) {
    setRenderedId(id)
    setRunNotStarted(false)
  }

  const displayName =
    (data?.name ?? '').replace(/^ZZ - DEMO - /i, '') || 'Demo Customer'
  const status = (data?.status ?? 'draft').toLowerCase()
  const statusConfig = IconStatusMapper[status] ?? IconStatusMapper['draft']

  const liveConfig = useMemo<DemoCustomerConfig>(
    () => ({
      companyName: displayName,
      payrollCycle: (data?.payrollCycle
        ? String(data.payrollCycle)
        : '12') as DemoCustomerConfig['payrollCycle'],
      benefitEffectiveDate: latestEnrollment?.benefitEffectiveDate ?? '',
      enrollmentStartDate: latestEnrollment?.enrollmentStartDate ?? '',
      enrollmentEndDate: latestEnrollment?.enrollmentEndDate ?? '',
    }),
    [data, displayName, latestEnrollment]
  )

  return (
    <Page
      title={isLoading ? '…' : displayName}
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Demo Companies', path: `/${DEMO_COMPANY_MANAGEMENT}` },
            { title: isLoading ? '…' : displayName },
          ]}
        />
      }
      action={
        isLoading ? undefined : (
          <Button
            type="default"
            onClick={() => browserHistory.push(`/${demoCompanyEditPath(id)}`)}
          >
            Edit
          </Button>
        )
      }
      headerContent={
        isLoading ? undefined : (
          <Tag icon={statusConfig.icon} color={statusConfig.color}>
            <Typography size={16} style={{ textTransform: 'capitalize' }}>
              {status}
            </Typography>
          </Tag>
        )
      }
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <DemoBanner description="You are viewing a demo company for a sales demonstration." />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 320px',
              gap: 16,
              alignItems: 'flex-start',
            }}
          >
            {/* ── Left: customer details + login credentials ── */}
            <Stack space={16}>
              <DemoCustomerSummary
                config={liveConfig}
                contacts={data?.contacts}
                enrollmentLoading={enrollmentLoading}
              />
              <DemoLoginCredentials
                companyName={data?.name}
                isPopulated={isPopulated}
                isLoading={populationLoading}
              />
            </Stack>

            {/* ── Right: progress + quote file ── */}
            <Stack space={16}>
              <DemoSetupProgress
                contributionStatus={contributionStatus}
                runNotStarted={runNotStarted}
                isLoading={contributionsLoading}
              />
              <QuoteFileUploadSection
                customerId={id}
                payrollCycle={liveConfig.payrollCycle}
                enrollmentLoading={enrollmentLoading}
                hasEnrollment={Boolean(latestEnrollment)}
                employeeCount={employeeCount}
                populationLoading={populationLoading}
                onPopulated={(populated) =>
                  setRunNotStarted(!populated.contributionRunJobId)
                }
              />
            </Stack>
          </div>

          <DemoPresetsModal />
        </>
      )}
    </Page>
  )
}
